import React, { useMemo, useState, useEffect } from 'react'; 
import { useDataQuery, useDataMutation, useDataEngine } from '@dhis2/app-runtime';
import { useAuth } from '../auth/AuthContext';
import {
  InputField,
  SingleSelect,
  SingleSelectOption,
  Button,
  CircularLoader,
  AlertBar,
  IconChevronDown16,
  IconChevronRight16,
} from '@dhis2/ui';
import { useNavigate } from 'react-router-dom';
import { getAuthHeader } from '../../utils/auth';
import { createTrackedEntityInstance, enrollTei } from '../GoalsManagement/hooks/DataContext'

// Add CSS styles for the modal and dropdowns
const styles = `
  .goal-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 999;
  }

  .goal-modal-container {
    background: white;
    border-radius: 10px;
    width: 600px;
    max-width: 90%;
    max-height: 90vh;
    overflow: visible;
    padding: 20px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.2);
    z-index: 1000;
    position: relative;
  }
  
  /* Ensure dropdowns appear above the modal */
  .goal-modal-container [class*="SelectMenu"] {
    z-index: 1001 !important;
  }
  
  /* Additional styling for the select containers */
  .select-container {
    position: relative;
    z-index: 1001;
  }
`;

// Inject styles
const styleSheet = document.createElement('style');
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);


const style = document.createElement('style');
style.textContent = `
  .dhis2-ui-select-menu,
  .dhis2-ui-menu,
  .dhis2-ui-filterable-menu,
  [class*="Menu"],
  [class*="menu"] {
    z-index: 1001 !important;
  }
`;
document.head.appendChild(style);

const ORG_GOALS_PROGRAM = 'NkReudUAW4F';
const ORG_GOALS_STAGE = 'VomTWbJot7U';

// Query program with assigned orgUnits
const PROGRAM_QUERY = {
  program: {
    resource: `programs/${ORG_GOALS_PROGRAM}`,
    params: { fields: "id,organisationUnits[id]" }
  }
};

// Mutation to assign program to an orgUnit
const ASSIGN_PROGRAM_MUTATION = {
  type: "create", // POST
  resource: `programs/${ORG_GOALS_PROGRAM}/organisationUnits`,
  data: ({ orgUnitId }) => ({ id: orgUnitId })
};


const GOALS_QUERY = {
  goals: {
    resource: 'events',
    params: {
      program: ORG_GOALS_PROGRAM, // Use separate program
      programStage: ORG_GOALS_STAGE,
      fields: 'event,orgUnit,eventDate,createdAt,updatedAt,dataValues[dataElement,value]',
      totalPages: true,
      pageSize: 1000
    }
  }
};


const DATA_ELEMENTS = {
  NAME: 'HJqqfzhH614', 
  DESCRIPTION: 'zS8QIgZc0Ll', 
  PROGRESS: 'pOOGDRzEo98', 
  START_DATE: 'MH7DQVegD1j', 
  END_DATE: 'pUvJQ9vy3A3', 
  STATUS: 'H5I924FiijC', 
  PRIORITY: 'Co2i5BleCcZ', 
  CATEGORY: 'uBPW95V2VoX',
  CREATED_BY: 'ir4nG6zZn4w' 
};


// Queries for all option sets
const OPTION_SET_QUERIES = {
  statusOptions: {
    resource: 'optionSets/rrB579k55jw',
    params: {
      fields: 'options[code,name]'
    }
  },
  priorityOptions: {
    resource: 'optionSets/DCt9jF8BhsP',
    params: {
      fields: 'options[code,name]'
    }
  },
  categoryOptions: {
    resource: 'optionSets/xvOTCmHoV3I',
    params: {
      fields: 'options[code,name]'
    }
  }
};

// Use /events endpoint for mutations
const GOAL_MUTATIONS = {
  CREATE_GOAL: {
    resource: 'events',
    type: 'create',
    data: ({ event }) => event
  },
  UPDATE_GOAL: {
    resource: 'events',
    type: 'update',
    id: ({ id }) => id,
    data: ({ data }) => data
  },
  DELETE_GOAL: {
    resource: 'events',
    type: 'delete',
    id: ({ id }) => id
  }
};

// Role definitions and permission functions
const ROLES = {
  ADMIN: "R3lFolCMdoi",
  CEO: "nLSSLch316I",
  HOD: "adeqQmxfCLF",
  TOP_STAFF: "gtMJNK0QVL7",
  SENIOR_STAFF: "vdVjNaqxblC",
  GUEST: "Guest"
};


export async function ensureProgramAssignment(engine, programId, orgUnitId) {
  try {
    // Fetch full program details, including required fields
    const programResponse = await engine.query({
      program: {
        resource: `programs/${programId}`,
        params: {
          fields: 'id,name,shortName,programType,dataElements[id],programStages[id],organisationUnits[id]'
        }
      }
    });

    const program = programResponse.program;

    // Check if orgUnit already assigned
    const alreadyAssigned = program.organisationUnits.some(ou => ou.id === orgUnitId);

    if (!alreadyAssigned) {
      // Add new orgUnit to organisationUnits
      const updatedOrganisationUnits = [...program.organisationUnits, { id: orgUnitId }];

      // Update the program by sending full required fields plus updated organisationUnits
      await engine.mutate({
        type: 'update',
        resource: `programs/${programId}`,
        data: {
          id: program.id,
          name: program.name,
          shortName: program.shortName,
          programType: program.programType,
          dataElements: program.dataElements,
          programStages: program.programStages,
          organisationUnits: updatedOrganisationUnits,
          // add other required properties if any
        }
      });

      console.log(`✅ Program ${programId} assigned to orgUnit ${orgUnitId}`);
    }
  } catch (err) {
    console.error("❌ Error ensuring program assignment", err);
    throw err;
  }
}



function canEdit(userRole, targetRole) {
  if (!userRole) return false;
  return userRole === targetRole || userRole === ROLES.ADMIN || userRole === ROLES.CEO;
}

function canDelete(userRole, targetRole) {
  if (!userRole) return false;
  return userRole === targetRole || userRole === ROLES.ADMIN || userRole === ROLES.CEO;
}

function GoalsManagementPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const engine = useDataEngine();
  const [goals, setGoals] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]); 
  const [priorityOptions, setPriorityOptions] = useState([]); 

  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [fiscalYearLoaded, setFiscalYearLoaded] = useState(false);

 const { 
    loading: queryLoading, 
    error: queryError, 
    data: queryData,
    refetch 
  } = useDataQuery(GOALS_QUERY);

  const { data: userData, loading: userLoading } = useDataQuery({
      me: {
        resource: 'me',
        params: { fields: 'id,username,displayName,organisationUnits[id,name,level]' }
      }
  });

  // USE COMBINED QUERY FOR ALL OPTION SETS
  const { 
    data: optionSetsData,
    loading: optionSetsLoading 
  } = useDataQuery(OPTION_SET_QUERIES);

  // Data Mutations
  const [createGoal] = useDataMutation(GOAL_MUTATIONS.CREATE_GOAL);
  const [updateGoal] = useDataMutation(GOAL_MUTATIONS.UPDATE_GOAL);
  const [deleteGoal] = useDataMutation(GOAL_MUTATIONS.DELETE_GOAL);

  // Helper function to extract value from dataValues array
  const getDataValue = (dataValues, dataElementId) => {
    const dataValue = dataValues.find(dv => dv.dataElement === dataElementId);
    return dataValue ? dataValue.value : '';
  };

  // Load goals from database tracker program
  useEffect(() => {
    if (queryData) {
      try {
        const events = queryData.goals.events || [];
        
        console.log('Raw events data:', events);
        
        // Transform events into goal objects
        const goalsData = events.map(event => ({
          id: event.event,
          name: getDataValue(event.dataValues, DATA_ELEMENTS.NAME),
          description: getDataValue(event.dataValues, DATA_ELEMENTS.DESCRIPTION),
          progress: parseInt(getDataValue(event.dataValues, DATA_ELEMENTS.PROGRESS)) || 0,
          startDate: getDataValue(event.dataValues, DATA_ELEMENTS.START_DATE),
          endDate: getDataValue(event.dataValues, DATA_ELEMENTS.END_DATE),
          status: getDataValue(event.dataValues, DATA_ELEMENTS.STATUS),
          priority: getDataValue(event.dataValues, DATA_ELEMENTS.PRIORITY),
          category: getDataValue(event.dataValues, DATA_ELEMENTS.CATEGORY),
          createdBy: getDataValue(event.dataValues, DATA_ELEMENTS.CREATED_BY) || ROLES.ADMIN,
          createdAt: event.createdAt,
          updatedAt: event.updatedAt,
          orgUnit: event.orgUnit
        }));
        
        console.log('Processed goals:', goalsData);
        setGoals(goalsData);
        setFilteredGoals(goalsData);
        setLoading(false);
      } catch (err) {
        console.error('Error parsing goals:', err);
        setError('Failed to parse goals data');
        setLoading(false);
      }
    }
  }, [queryData]);

  useEffect(() => {
    if (queryError) {
      console.error('Query error:', queryError);
      setError('Failed to load goals from server');
      setLoading(false);
    }
  }, [queryError]);

    

useEffect(() => {

  const currentOrgUnitId = userData?.me?.organisationUnits?.[0]?.id;
  
  // Fallback immediately if no orgUnitId
  if (!currentOrgUnitId) {
    const now = new Date();
    setFiscalYear({ start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` });
    setFiscalYearLoaded(true);
    return;
  }

  if (!engine) return;

  const loadFiscalYear = async () => {
    try {
      // Get Staff TEI from enrollments
      const enrollmentsResponse = await engine.query({
        enrollmentsQuery: {
          resource: 'enrollments',
          params: {
            ou: currentOrgUnitId, // ✅ USE currentOrgUnitId (NOT orgUnitId)
            program: 'NkReudUAW4F',
            fields: 'trackedEntityInstance',
            pageSize: 1,
            order: 'lastUpdated:desc'
          }
        }
      });
      
      const teiId = enrollmentsResponse.enrollmentsQuery?.enrollments?.[0]?.trackedEntityInstance;
      if (!teiId) {
        console.warn('⚠️ No Staff TEI found - using current year fallback');
        const now = new Date();
        setFiscalYear({ start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` });
        setFiscalYearLoaded(true);
        return;
      }
      
      // Query metadata event
      const eventsResponse = await engine.query({
        eventsQuery: {
          resource: 'events',
          params: {
            program: 'NkReudUAW4F',
            programStage: 'htIWgNQWEMm',
            ou: currentOrgUnitId, // ✅ USE currentOrgUnitId
            trackedEntityInstance: teiId,
            pageSize: 1,
            order: 'eventDate:desc',
            fields: 'dataValues[dataElement,value]'
          }
        }
      });
      
      const event = eventsResponse.eventsQuery?.events?.[0];
      let start = '', end = '';
      
      if (event) {
        event.dataValues?.forEach(dv => {
          if (dv.dataElement === 'U55MqwBcRAe') start = dv.value;
          if (dv.dataElement === 'kgdk4NXu4f5') end = dv.value;
        });
      }
      
      if (start && end) {
        setFiscalYear({ start, end });
        console.log('✅ Fiscal year loaded from meta', { start, end });
      } else {
        const now = new Date();
        setFiscalYear({ start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` });
      }
      setFiscalYearLoaded(true);
    } catch (error) {
      console.error('❌ Failed to load fiscal year:', error);
      const now = new Date();
      setFiscalYear({ start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` });
      setFiscalYearLoaded(true);
    }
  };
  
  loadFiscalYear();
}, [userData, engine, fiscalYearLoaded]); // ✅ DEPEND ON userData (NOT orgUnitId)

  
  useEffect(() => {
    if (optionSetsData) {
      try {
        // Process status options
        const statusOpts = optionSetsData.statusOptions.options || [];
        const formattedStatusOptions = statusOpts.map(option => ({
          code: option.code,
          name: option.name
        }));
        setStatusOptions(formattedStatusOptions);

        // Process priority options
        const priorityOpts = optionSetsData.priorityOptions.options || [];
        const formattedPriorityOptions = priorityOpts.map(option => ({
          code: option.code,
          name: option.name
        }));
        setPriorityOptions(formattedPriorityOptions);

        // Process category options
        const categoryOpts = optionSetsData.categoryOptions.options || [];
        const formattedCategoryOptions = categoryOpts.map(option => ({
          code: option.code,
          name: option.name
        }));
        setCategoryOptions(formattedCategoryOptions);

        console.log('All options loaded:', {
          status: formattedStatusOptions,
          priority: formattedPriorityOptions,
          category: formattedCategoryOptions
        });
      } catch (err) {
        console.error('Error parsing option sets:', err);
      }
    }
  }, [optionSetsData]);

    // Filter goals based on search term and filters
    useEffect(() => {
      if (!goals.length) return;
      
      let result = goals;
      
      // Apply search filter
      if (searchTerm) {
        result = result.filter(goal => 
          goal.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          goal.description.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      if (statusFilter !== 'all') {
        result = result.filter(goal => goal.status === statusFilter);
      }
      
      if (priorityFilter !== 'all') {
        result = result.filter(goal => goal.priority === priorityFilter);
      }

      if (categoryFilter !== 'all') {
          result = result.filter(goal => goal.category === categoryFilter);
      }
      
      setFilteredGoals(result);
    }, [goals, searchTerm, statusFilter, priorityFilter]);

    const handleCreate = () => {
      setEditingGoal(null);
      setShowModal(true);
   };

  const handleEdit = (goal) => {
    if (!canEdit(userRoleId, goal.createdBy)) {
      alert('You do not have permission to edit this goal');
      return;
    }
    setEditingGoal(goal);
    setShowModal(true);
  };

  const handleDelete = async (goalId, createdBy) => {
    if (!canDelete(userRoleId, createdBy)) {
      alert('You do not have permission to delete this goal');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this goal?')) {
      try {
        await deleteGoal({ id: goalId });
        await refetch();
        alert('Goal deleted successfully');
        window.location.reload();
      } catch (error) {
        console.error('Failed to delete goal:', error);
        alert('Failed to delete goal');
      }
    }
  };

  const findStaffTeiByUsername = async (username, orgUnitId, authHeader) => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_DHIS2_BASE_URL || window.location.origin}/api/trackedEntityInstances?trackedEntityType=OaAbpIs1cV7&ou=${orgUnitId}&ouMode=ACCESSIBLE&filter=g3RRIfDiQra:eq:${username}&fields=trackedEntityInstance`,
          {
            headers: authHeader
          }
        );
        
        if (!response.ok) {
          throw new Error('Failed to fetch TEI');
        }
        
        const data = await response.json();
        return data.trackedEntityInstances?.[0]?.trackedEntityInstance;
      } catch (error) {
        console.error('Error finding staff TEI:', error);
        return null;
      }
   };


const handleSave = async (goalData) => {
  try {
    console.log('[handleSave] Starting goal save process');
    const eventDate = goalData.startDate || new Date().toISOString().split('T')[0];
    const orgUnit = user.organisationUnits?.[0]?.id;

    if (!orgUnit) {
      alert('User must be assigned to an organisation unit to create goals');
      return;
    }
    console.log('[handleSave] OrgUnit:', orgUnit);

    // Ensure program assigned to org unit
    console.log('[handleSave] Ensuring program assignment');
    await ensureProgramAssignment(engine, ORG_GOALS_PROGRAM, orgUnit);
    console.log('[handleSave] Program assignment ensured');

    // Fetch user auth header for API calls
    const authHeader = getAuthHeader();
    console.log('[handleSave] Auth header fetched');

    // Prepare data values
    const dataValues = [
      { dataElement: DATA_ELEMENTS.NAME, value: goalData.name },
      { dataElement: DATA_ELEMENTS.DESCRIPTION, value: goalData.description },
      { dataElement: DATA_ELEMENTS.PROGRESS, value: goalData.progress.toString() },
      { dataElement: DATA_ELEMENTS.START_DATE, value: goalData.startDate },
      { dataElement: DATA_ELEMENTS.END_DATE, value: goalData.endDate },
      { dataElement: DATA_ELEMENTS.STATUS, value: goalData.status },
      { dataElement: DATA_ELEMENTS.PRIORITY, value: goalData.priority },
      { dataElement: DATA_ELEMENTS.CATEGORY, value: goalData.category },
      ...(editingGoal ? [] : [{ dataElement: DATA_ELEMENTS.CREATED_BY, value: userRoleId || ROLES.ADMIN }])
    ];
    console.log('[handleSave] Data values prepared:', dataValues);

    // Prepare event payload WITHOUT trackedEntityInstance and enrollment
    // This assumes goals are organization-level events not tied to specific staff
    const eventPayload = {
      program: ORG_GOALS_PROGRAM,
      programStage: ORG_GOALS_STAGE,
      orgUnit,
      eventDate,
      status: 'COMPLETED', // or 'ACTIVE' depending on your workflow
      dataValues,
    };
    console.log('[handleSave] Event payload prepared:', eventPayload);

    if (editingGoal) {
      console.log('[handleSave] Updating existing goal with ID:', editingGoal.id);
      
      // For updates, we need to include the event ID
      const updatePayload = {
        event: editingGoal.id,
        ...eventPayload,
        orgUnit: editingGoal.orgUnit // Keep original org unit for updates
      };
      
      await updateGoal({
        id: editingGoal.id,
        data: updatePayload,
      });
      console.log('[handleSave] Goal updated successfully');
    } else {
      console.log('[handleSave] Creating new goal');
      await createGoal({ event: eventPayload });
      console.log('[handleSave] Goal created successfully');
    }

    console.log('[handleSave] Refetching data');
    await refetch();
    console.log('[handleSave] Data refetched');
    
    setShowModal(false);
    alert(`Goal ${editingGoal ? 'updated' : 'created'} successfully`);
    console.log('[handleSave] Goal save process completed successfully');

  } catch (error) {
    console.error('[handleSave] Failed to save goal:', error);
    console.error('[handleSave] Error stack:', error.stack);
    
    // Check for specific error messages
    if (error.message?.includes('trackedEntityInstance')) {
      alert('This program requires a tracked entity instance. Please check program configuration or contact administrator.');
    } else if (error.message?.includes('enrollment')) {
      alert('Enrollment error. Please check if the program is properly configured.');
    } else {
      alert('Failed to save goal. Check console for details.');
    }
  }
};

  // Extract user role
  const userRoleId = user?.roleIds?.[0] || 
                     user?.userCredentials?.userRoleInfo?.id || 
                     user?.userCredentials?.roles?.[0]?.id || 
                     user?.role || 
                     user?.userRole;
  
  // Check if user is admin or CEO
  const isAdmin = userRoleId === ROLES.ADMIN;
  const isCEO = userRoleId === ROLES.CEO;
  const userCanCreateGoals = isAdmin || isCEO;
  

  if (!userCanCreateGoals) {
    return (
      <div style={{ 
        maxWidth: 700, 
        margin: 'auto', 
        padding: 40, 
        textAlign: 'center' 
      }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to access the goals management page.</p>
         <p>Required roles: Admin or CEO </p>
        <Button onClick={() => navigate('/dashboard')}>
          Return to Dashboard
        </Button>
      </div>
    );
  }

  if (loading || queryLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '50vh' 
      }}>
        <CircularLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: 700, margin: 'auto', padding: 20 }}>
        <AlertBar critical>{error}</AlertBar>
        <Button onClick={() => refetch()} style={{ marginTop: 20 }}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 1200,
      margin: "0 auto",
      padding: "20px",
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "30px",
        paddingBottom: "15px",
        borderBottom: "1px solid #e0e0e0"
      }}>
        <h2 style={{ color: "#297934ff", margin: 0 }}>Company Goals Management</h2>
        <div>
          <span style={{
            padding: "4px 8px",
            borderRadius: "20px",
            fontSize: "0.8rem",
            color: "white"
          }}>
            {isAdmin ? 'Administrator' : 'Chief Executive Director'}
          </span>
        </div>
      </div>
      
      <div style={{
        backgroundColor: "white",
        borderRadius: "10px",
        boxShadow: "0 8px 12px rgba(0,0,0,0.1)",
        padding: "20px",
        marginBottom: "20px"
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px"
        }}>
          <h3 style={{ margin: 0 }}>Company Goals</h3>
          <Button primary onClick={handleCreate}>
            + Add Goal
          </Button>
        </div>
        
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
          <InputField
            placeholder="Search goals..."
            value={searchTerm}
            onChange={({ value }) => setSearchTerm(value)}
            style={{ flex: 1 }}
          />
        </div>
        
        <div style={{ display: "flex", gap: "10px", marginBottom: "20px", flexWrap: "wrap" }}>
         {statusOptions.length > 0 ? (
              <SingleSelect
                selected={statusFilter}
                onChange={({ selected }) => setStatusFilter(selected)}
                placeholder="Filter by status"
                style={{ minWidth: "180px" }}
                filterable
                loading={optionSetsLoading}
                menuZIndex={2000}
              >
                <SingleSelectOption value="all" label="All Statuses" />
                {statusOptions.map(option => (
                  <SingleSelectOption 
                    key={option.code} 
                    value={option.code} 
                    label={option.name} 
                  />
                ))}
              </SingleSelect>
            ) : (
              <CircularLoader size="small" />
            )}
          
          <SingleSelect
                placeholder="Filter by priority"
                selected={priorityFilter}
                onChange={({ selected }) => setPriorityFilter(selected)}
                style={{ minWidth: "180px" }}
                filterable
                loading={optionSetsLoading}
                menuZIndex={2000}
            >
               <SingleSelectOption value="all" label="All Priorities" />
                {priorityOptions.map(option => (
                <SingleSelectOption 
                    key={option.code} 
                    value={option.code} 
                    label={option.name} 
                />
                ))}
            </SingleSelect>
            <SingleSelect
                placeholder="Filter by category"
                selected={categoryFilter}
                onChange={({ selected }) => setCategoryFilter(selected)}
                style={{ minWidth: "180px" }}
                filterable
                loading={optionSetsLoading}
                menuZIndex={2000}
            >
                <SingleSelectOption value="all" label="All Categories" />
                {categoryOptions.map(option => (
                <SingleSelectOption 
                    key={option.code} 
                    value={option.code} 
                    label={option.name} 
                />
                ))}
            </SingleSelect>
          </div>
        
        {filteredGoals.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Name</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Description</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Target</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Timeline</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Status</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Priority</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Category</th>
                  <th style={{ padding: "12px 15px", textAlign: "left", borderBottom: "1px solid #e0e0e0", backgroundColor: "#f8f9fa", fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredGoals.map(goal => {
                  const userCanEdit = canEdit(userRoleId, goal.createdBy);
                  const userCanDelete = canDelete(userRoleId, goal.createdBy);
                  
                  return (
                    <tr key={goal.id} style={{ borderBottom: "1px solid #e0e0e0" }}>
                      <td style={{ padding: "12px 15px" }}>{goal.name}</td>
                      <td style={{ padding: "12px 15px" }}>{goal.description}</td>
                      <td style={{ padding: "12px 15px" }}>
                        <div style={{
                          height: "8px",
                          backgroundColor: "#e0e0e0",
                          borderRadius: "4px",
                          overflow: "hidden",
                          margin: "10px 0"
                        }}>
                          <div style={{
                            height: "100%",
                            backgroundColor: "#2ecc71",
                            width: `${goal.progress}%`
                          }}></div>
                        </div>
                        <div>{goal.progress}%</div>
                      </td>
                      <td style={{ padding: "12px 15px" }}>
                        {goal.startDate ? new Date(goal.startDate).toLocaleDateString() : 'N/A'} - 
                        {goal.endDate ? new Date(goal.endDate).toLocaleDateString() : 'N/A'}
                      </td>
                      <td style={{ padding: "12px 15px" }}>
                        <span style={{
                            padding: "4px 8px",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            backgroundColor: goal.status === "active" ? "#11ae20ff" : 
                                            goal.status === "completed" ? "#2e38ccff" : "#f39c12",
                            color: "white"
                        }}>
                            {goal.status ? 
                            (statusOptions.find(opt => opt.code === goal.status)?.name || goal.status) : 
                            'Not set'}
                        </span>
                      </td>
                      <td style={{ padding: "12px 15px" }}>
                        <span style={{
                            padding: "4px 8px",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            backgroundColor: goal.priority === "high" ? "#e74c3c" : 
                                            goal.priority === "medium" ? "#f39c12" : "#2ecc71",
                            color: "white"
                        }}>
                            {goal.priority ? 
                            (priorityOptions.find(opt => opt.code === goal.priority)?.name || goal.priority) : 
                            'Not set'}
                        </span>
                        </td>
                      <td style={{ padding: "12px 15px" }}>
                        <span style={{
                            padding: "4px 8px",
                            borderRadius: "20px",
                            fontSize: "0.8rem",
                            
                            color: "black"
                        }}>
                            {goal.category ? 
                            (categoryOptions.find(opt => opt.code === goal.category)?.name || goal.category) : 
                            'Not set'}
                        </span>
                        </td>
                      <td style={{ padding: "12px 15px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                          <Button 
                            small
                            onClick={() => handleEdit(goal)}
                            disabled={!userCanEdit}
                          >
                            Edit
                          </Button>
                          <Button 
                            small
                            destructive
                            onClick={() => handleDelete(goal.id, goal.createdBy)}
                            disabled={!userCanDelete}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "#7f8c8d" }}>
            <IconChevronDown16 style={{ fontSize: "3rem", marginBottom: "15px", color: "#bdc3c7" }} />
            <h3>No goals found</h3>
            <p>Create your first organization goal to get started</p>
          </div>
        )}
      </div>
      
      {showModal && (
          <GoalModal 
            goal={editingGoal}
            onSave={handleSave}
            onClose={() => setShowModal(false)}
            currentUser={user}
            canEdit={editingGoal ? canEdit(userRoleId, editingGoal.createdBy) : true}
            statusOptions={statusOptions}
            priorityOptions={priorityOptions}
            categoryOptions={categoryOptions}
            fiscalYear={fiscalYear} // ← Add this line
          />
        )}
            </div>
  );
}

function GoalModal({ goal, onSave, onClose, currentUser, canEdit, statusOptions, priorityOptions, categoryOptions, fiscalYear }) {


  const [name, setName] = useState(goal ? goal.name : "");
  const [description, setDescription] = useState(goal ? goal.description : "");
  const [progress, setProgress] = useState(goal ? goal.progress : 0);
  const [startDate, setStartDate] = useState(goal ? goal.startDate : fiscalYear.start);
  const [endDate, setEndDate] = useState(goal ? goal.endDate : fiscalYear.end);
  const [status, setStatus] = useState(goal ? goal.status : (statusOptions[0]?.code || ""));
  const [priority, setPriority] = useState(goal ? goal.priority : (priorityOptions[0]?.code || ""));
  const [category, setCategory] = useState(goal ? goal.category : (categoryOptions[0]?.code || ""));
  const [saving, setSaving] = useState(false);

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (goal && !canEdit) {
      alert('You do not have permission to edit this goal');
      return;
    }
    
    setSaving(true);
    
    try {
      await onSave({
        name,
        description,
        progress: parseInt(progress),
        startDate,
        endDate,
        status,
        priority,
        category,
      });
    } catch (error) {
      console.error('Failed to save goal:', error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
      if (category && category.length > 0) {
        const filtered = categoryOptions.filter(option =>
          option.name.toLowerCase().includes(category.toLowerCase()) ||
          option.code.toLowerCase().includes(category.toLowerCase())
        );
        setFilteredSuggestions(filtered.slice(0, 5)); // Show max 5 suggestions
        setShowSuggestions(filtered.length > 0);
      } else {
        setShowSuggestions(false);
      }
    }, [category, categoryOptions]);

  const renderFormField = (label, children, required = true) => (
        <div style={{ marginBottom: '5px' }}>
            <div style={{ fontWeight: '400', marginBottom: '4px', fontSize: '14px' }}>
                {label} {required && <span style={{ color: 'red' }}>*</span>}
            </div>
            {children}
        </div>
    );

  return (
    <div className="goal-modal-overlay">
      <div className="goal-modal-container">
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px"
        }}>
          <h3 style={{ margin: 0 }}>{goal ? "Edit Goal" : "Create New Goal"}</h3>
          <button
            style={{
               backgroundColor: "#f34034ff",
                        color: "#000",
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                        borderRadius: '5px',
                        width: '100px',
                        height: '35px',
                        color: '#ffffffff',
                        cursor: 'pointer',
               }} 
            onClick={onClose}
          >
            Close
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <InputField
            label="Goal Name"
            value={name}
            onChange={({ value }) => setName(value)}
            required
            style={{ marginBottom: "15px" }}
            readOnly={goal && !canEdit}
          />

          <InputField
            label="Description"
            value={description}
            onChange={({ value }) => setDescription(value)}
            required
            multiline
            rows={4}
            style={{ marginBottom: "15px" }}
            readOnly={goal && !canEdit}
          />

          <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
           <div style={{ flex: 1 }}>
            <InputField
              label="Target (%)"
              type="number"
              min="0"
              max="100"
              value={progress}
              onChange={({ value }) => setProgress(value)}
              style={{ marginBottom: "15px" }}
              readOnly={goal && !canEdit}
            />
             </div>
             <div style={{ flex: 1 }}>      
            <InputField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={({ value }) => setStartDate(value)}
              required
              style={{ flex: 1 }}
              readOnly={goal && !canEdit}
            />
             </div>
             <div style={{ flex: 1 }}>
            <InputField
              label="End Date"
              type="date"
              value={endDate}
              onChange={({ value }) => setEndDate(value)}
              required
              style={{ flex: 1 }}
              readOnly={goal && !canEdit}
            />
          </div>
          </div>
          <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
            <div style={{ flex: 1 }}>
              {renderFormField('Status', (
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  disabled={goal && !canEdit}
                  required
                  style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white' }}
                >
                  {statusOptions.map(option => (
                    <option key={option.code} value={option.code}>{option.name}</option>
                  ))}
                </select>
              ))}
            </div>
             <div style={{ flex: 1 }}>
               {renderFormField('Priority', (
                 <select
                   value={priority}
                   onChange={(e) => setPriority(e.target.value)}
                   disabled={goal && !canEdit}
                   required
                   style={{ width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white' }}
                 >
                   {priorityOptions.map(option => (
                     <option key={option.code} value={option.code}>{option.name}</option>
                   ))}
                 </select>
               ))}
             </div>
            <div style={{ flex: 1 }}>
              {renderFormField('Category', (
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 45) {
                        setCategory(value);
                      }
                    }}
                    onFocus={() => {
                      if (category.length > 0) {
                        setShowSuggestions(true);
                      }
                    }}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    disabled={goal && !canEdit}
                    required
                    style={{ 
                      width: '100%', 
                      maxWidth: 'calc(100% - 70px)', // Leave space for counter
                      padding: '8px 70px 8px 8px', // More padding on right
                      border: '1px solid #ccc', 
                      borderRadius: '4px', 
                      backgroundColor: goal && !canEdit ? '#f5f5f5' : 'white',
                      fontSize: '14px',
                      outline: 'none'
                    }}
                    maxLength={45}
                    placeholder="Type or select category"
                  />
                  
                  {/* Character counter */}
                  <div style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: '11px',
                    color: category.length === 45 ? '#d32f2f' : '#888',
                    fontWeight: category.length === 45 ? 'bold' : 'normal',
                    pointerEvents: 'none'
                  }}>
                    {category.length}/45
                  </div>
                  
                  {/* Suggestions dropdown */}
                  {showSuggestions && filteredSuggestions.length > 0 && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #e0e0e0',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 1000,
                      maxHeight: '150px',
                      overflowY: 'auto',
                      marginTop: '2px'
                    }}>
                      {filteredSuggestions.map((option, index) => (
                        <div
                          key={option.code}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            borderBottom: index < filteredSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                            backgroundColor: '#fff',
                            fontSize: '14px',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}
                          onClick={() => {
                            setCategory(option.name);
                            setShowSuggestions(false);
                          }}
                        >
                          {option.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end", marginTop: "40px" }}>
            <button 
              style={{
               backgroundColor: "#fac746ff",
                        color: "#000",
                        border: 'none',
                        boxShadow: '0 2px 4px rgba(122, 118, 118, 1)',
                        borderRadius: '5px',
                        width: '100px',
                        height: '35px',
                        color: '#ffffffff',
                        cursor: 'pointer',
               }}
              onClick={onClose}
            >
              Cancel
            </button>
            {(!goal || canEdit) && (
              <Button 
                primary 
                type="submit"
                loading={saving}
              >
                {goal ? "Update Goal" : "Create Goal"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default GoalsManagementPage;